import type { AdaptedPatientConsent } from "@/lib/helpers/adaptConsent";
import { adaptPatientConsent } from "@/lib/helpers/adaptConsent";
import { ADVANCED_CARE_PROGRAM } from "@/core/constants";
import type { PatientConsent } from "@/core/types";

import type { ACPStatus } from "../types";

import { getACPStatus } from "./program-status";

export type ACPConsentVersionRow = {
  consent: AdaptedPatientConsent | null;
  consentStatus: ACPStatus;
  version: string;
};

type GetACPConsentRowsParams = {
  consents: PatientConsent[];
  currentVersion: string;
  insuranceCarrier?: string;
};

/**
 * Builds one row per ACP consent version the patient has a record for, plus the currently
 * published version if the patient has no record for it yet. Sorted newest version first.
 *
 * Historical versions before the patient joined the program are never synthesized — a row only
 * exists for a superseded version if the patient actually has a record for it. Only the current
 * version can appear without a record (rendered as NOT_RESPONDED).
 *
 * Assumes the caller has already confirmed the patient is ACP-eligible — eligibility is
 * carrier-based, not version-based, so it's handled once by the caller instead of per row.
 */
export function getACPConsentRows({
  consents,
  currentVersion,
  insuranceCarrier,
}: GetACPConsentRowsParams): ACPConsentVersionRow[] {
  const latestByVersion = getLatestACPConsentByVersion(consents);
  const versions = new Set([...Object.keys(latestByVersion), currentVersion]);

  return Array.from(versions)
    .sort((a, b) => Number(b) - Number(a))
    .map((version) => {
      const consent = latestByVersion[version] ?? null;
      const consentStatus = getACPStatus({
        consentResponse: consent?.consentResponse,
        insuranceCarrier,
      });

      return { consent, consentStatus, version };
    });
}

function getLatestACPConsentByVersion(consents: PatientConsent[]) {
  const acpConsents = consents
    .filter((consent) => consent.consent_form_name === ADVANCED_CARE_PROGRAM)
    .sort((a, b) => Number(a.created_date) - Number(b.created_date));

  const latestByVersion: Record<string, AdaptedPatientConsent> = {};

  for (const consent of acpConsents) {
    latestByVersion[consent.consent_form_version] = adaptPatientConsent(consent);
  }

  return latestByVersion;
}
